// ── Readiness module types ───────────────────────────────────────────────────
// Mirrors the Iteration 1 brief (§2 content model, §3 provenance, Engineering
// Note 2 tables). Everything is scoped by org_id + index_id.

export type LevelCode = 'L0' | 'L1' | 'L2' | 'L3'
export const LEVELS: LevelCode[] = ['L0', 'L1', 'L2', 'L3']
export const levelNum = (l: LevelCode): number => Number(l[1])
export const levelFromNum = (n: number): LevelCode => `L${Math.max(0, Math.min(3, n))}` as LevelCode

export type Transition = 'L0>L1' | 'L1>L2' | 'L2>L3' | 'L3'
export const transitionFor = (level: LevelCode): Transition =>
  level === 'L3' ? 'L3' : (`${level}>${levelFromNum(levelNum(level) + 1)}` as Transition)

// ── Index definition (§2.2) ─────────────────────────────────────────────────

export interface LevelScaleEntry {
  code: LevelCode
  name: string
  description: string
  ref?: string | null
}

export interface GlossaryEntry {
  term: string
  text: string
  ref?: string | null
}

// Scale `levels` (QRI): descriptors per level and recommendations per
// transition. Scale `score_0_10` (QPI): tier statements with their source page
// and mapped actions; `levels` / `recommendations` are then empty.
export interface TierDef {
  n: number // 1-based
  statement: string
  source_page: string
}

export interface ActionDef {
  kind: 'stage_action' | 'recommendation' | 'metric'
  text: string
  source_page: string
  stage?: string | null
  start_today?: string | null
  pitfall?: string | null
}

export interface ObjectiveDef {
  code: string
  title: string
  no_regrets: boolean
  description: string
  rationale: string
  levels: Record<LevelCode, string>
  recommendations: Record<Transition, string[]>
  tiers?: TierDef[]
  actions?: ActionDef[]
  benchmark?: { sector: number; retail?: number | null; non_retail?: number | null } | null
}

export interface DomainDef {
  code: string
  name: string
  order: number
  color_dark: string
  color_light: string
  summary: string
  benchmark?: { sector: number; retail?: number | null; non_retail?: number | null } | null
  objectives: ObjectiveDef[]
}

// Iteration 2 §11.2: how an index measures (`levels` = discrete L0–L3 with
// descriptors; `score_0_10` = numeric score per sub-index with optional tiers;
// `stages` reserved for roadmap-style guidance, not implemented).
export type IndexScale = 'levels' | 'score_0_10' | 'stages'
export type IndexStatus = 'active' | 'preliminary' | 'coming_soon'
export type IndexAggregation = 'none' | 'native'

export interface IndexBenchmarks {
  label: 'sector' | 'retail' | 'non-retail'
  source: string
  overall: number
  by_label?: Record<string, number>
}

export interface StartingSetItem {
  id: string
  text: string
  source_page?: string | null
  objective_codes: string[]
  signal?: string | null // auto-satisfied by this signal when present (Open Question 5)
}

export interface StartingSet {
  label: string
  render: 'filter' | 'checklist'
  items: StartingSetItem[]
}

export interface IndexDef {
  id: string
  name: string
  regulator: string // short regulator name, the primary identifier in the selector (§12)
  long_name: string
  jurisdiction: string
  publisher: string
  version: string
  published_at: string | null
  status: IndexStatus
  scale: IndexScale
  aggregation: IndexAggregation
  flag: string // inline 20 × 14 SVG markup (§12), never emoji
  report?: { name: string; short: string; url: string | null } | null
  preliminary?: { notice: string; help_route: string } | null
  milestone?: { date: string; label: string } | null // chart marker when no submission deadline
  benchmarks?: IndexBenchmarks | null
  starting_set?: StartingSet | null
  transcription: { status: string; note: string }
  submission: {
    label: string
    deadline: string
    template_id: string
    template_name: string
  } | null
  handbook: { name: string; url: string | null } | null
  // Iteration 3 §5: index prior dwell per transition (days), rung 4 of the ladder.
  projection?: { priors: Partial<Record<Transition, number>> } | null
  level_scale: LevelScaleEntry[]
  glossary: GlossaryEntry[]
  domains: DomainDef[]
}

// ── Objective state + provenance (§3.2, Note 2) ─────────────────────────────

export type LevelStatus = 'suggested' | 'confirmed' | 'declared' | 'unset'

// Iteration 2 §6: whether a level rests on internal judgement or on the PDF
// CSA's official questionnaire produced.
export type LevelBasis = 'internal' | 'csa_report'

export interface ObjectiveState {
  org_id: string
  index_id: string
  objective_code: string
  level: LevelCode
  status: LevelStatus
  set_by: string | null
  set_at: string | null
  note: string | null
  // A sync writes a differing suggestion here and leaves `level` alone (Note 4).
  suggested_level: LevelCode | null
  suggested_at: string | null
  suggested_reason: string | null
  owner: string | null
  target_level_text: string | null
  due_at: string | null
  basis: LevelBasis
  // Scale `score_0_10` only (§11): the sub-index score (0–10, one decimal) and
  // the tier it was set from (null when a different score was entered).
  score: number | null
  tier: number | null
  suggested_score: number | null
  suggested_tier: number | null
}

// ── Evidence (§3.3) ─────────────────────────────────────────────────────────

export type EvidenceSource = 'discovery' | 'playbook' | 'manual'

export interface Evidence {
  id: string
  org_id: string
  index_id: string
  objective_code: string
  source: EvidenceSource
  external_id: string | null
  title: string
  summary: string
  link: string | null
  occurred_at: string
  added_by: string // 'system' or a user name
  archived_at: string | null
  kind?: 'file' | 'note'
  file_name?: string | null
  source_label?: string // a served item's own label (Readiness, Risk, Link…), where source alone would misname it
}

// ── Snapshots (§9) ──────────────────────────────────────────────────────────

export interface SnapshotItem {
  snapshot_id: string
  objective_code: string
  level: LevelCode
  status: LevelStatus
  evidence_ids: string[]
  basis?: LevelBasis
  score?: number | null // scale score_0_10
  previous_level?: LevelCode | null // level in the previous assessment (§10.2)
}

// Discovery totals captured when an assessment is saved (§9).
export interface DiscoverySummary {
  assets_total: number
  assets_quantum_safe: number
  assets_not_migrated: number
  sources: number
  last_scan_at: string | null
}

// An assessment (§1.2): a saved, point-in-time record of the confirmed levels
// and their evidence. Immutable; carries created_by / created_at stamped by the
// system; no sign-off and no submission state. The table keeps the
// `readiness_snapshot` name (Engineering may keep it, §1.2).
export interface Snapshot {
  id: string
  org_id: string
  index_id: string
  index_version: string
  name: string
  note: string | null
  score: number
  live_score?: number // score incl. suggested levels at save time
  created_by: string // user name or 'system'
  created_at: string
  as_of: string // the date the record is "as of" (may be back-dated, §8.1)
  baseline: boolean
  scheduled: boolean
  items: SnapshotItem[]
  discovery_summary: DiscoverySummary | null
  // Iteration 3 Note 2: the organisation projection summary stored at save time,
  // with the vendor gates it used, so replaying the log reproduces it.
  projection?: { summary: import('@/lib/readiness/projection').ProjectionSummary; gates: Record<string, string> } | null
}

// ── Objective event log (Iteration 2 §2) ────────────────────────────────────
// Append-only; every write to the objective state or evidence emits one event
// in the same transaction. `state_at(date)` replays it.

export type ObjectiveEventType =
  | 'suggested'
  | 'confirmed'
  | 'declared'
  | 'overridden'
  | 'evidence_added'
  | 'evidence_archived'
  // Iteration 5 §13: an archived item brought back after the undo window.
  | 'evidence_restored'
  | 'due_set'
  | 'owner_set'
  | 'basis_set'
  | 'assessment_saved'
  // Iteration 3 §4: Discovery found more assets in an objective's scope.
  | 'scope_expanded'
  // Iteration 3 §12: a catalogue write moved, detected or readied a dependency.
  | 'dependency_changed'

// Iteration 3 §4: why a level fell. Coverage-driven = the known scope grew;
// posture-driven = something got worse (the default).
export type ChangeCause = 'coverage' | 'posture'

export type DependencyChangeKind = 'vendor_date_moved' | 'dependency_detected' | 'dependency_ready'

export interface ObjectiveEvent {
  id: string
  org_id: string
  index_id: string
  objective_code: string | null // null for assessment_saved
  event_type: ObjectiveEventType
  from_level: LevelCode | null
  to_level: LevelCode | null
  score_from: number | null
  score_to: number | null
  evidence_id: string | null
  actor: string // user id or 'system'
  occurred_at: string
  note: string | null
  // Convenience payload for replay: provenance of the resulting level.
  status?: LevelStatus
  tier?: number | null
  basis?: LevelBasis
  reason?: string | null
  // scope_expanded (I3 §4.1)
  assets_known_from?: number | null
  assets_known_to?: number | null
  source?: string | null
  // Downward suggested / overridden / declared events (I3 §4.1)
  cause?: ChangeCause | null
  // dependency_changed (I3 §12.2)
  dependency?: { kind: DependencyChangeKind; technology_id: string; technology: string; capability: string | null; old: string | null; new: string | null; asset: string | null } | null
}

// ── Sync (§7, Note 4; Iteration 5 §16) ──────────────────────────────────────

// One line of the sync change summary (I5 §16.2). Groups: levels, evidence,
// coverage, dependencies, migration items. `to` is where the change lives.
export type SyncChange =
  | { group: 'levels'; kind: 'suggested' | 'flag' | 'coverage'; objective_code: string; from_level: LevelCode | null; to_level: LevelCode | null; confirmed_level?: LevelCode | null }
  | { group: 'evidence'; objective_code: string; count: number; source: EvidenceSource }
  | { group: 'coverage'; from: number; to: number }
  | { group: 'dependencies'; kind: 'vendor_date_moved' | 'technology_detected' | 'unmatched'; technology_id: string | null; text: string }
  | { group: 'items'; item_id: string; code: string; outcome: 'cleared' | 'still_vulnerable' }

// `readiness_sync_run` (I5 Note 8): the run keeps the changes it wrote so the
// summary can be reopened from the strip and survives leaving the page.
export interface SyncRun {
  id: string
  org_id: string
  index_id: string
  index?: number
  started_at: string
  finished_at: string | null
  status: 'running' | 'succeeded' | 'failed'
  error: string | null
  discovery_connected: boolean
  // Whether qtz-readiness's Playbook read model answered (I7); absent on runs before it existed.
  playbook_connected?: boolean
  evidence_created: number
  suggestions_written: number
  // `scheduled` = the daily job; `on_open` = the catch-up sync when the page
  // opens with the last run over a day old (I5 §16.2).
  trigger: 'manual' | 'scheduled' | 'on_open'
  changes?: SyncChange[]
  // Archived evidence items whose source fact still holds; never re-created (I5 §13.2).
  evidence_skipped_archived?: number
}

// ── Playbook linking (Note 6) ───────────────────────────────────────────────

export type LinkStatus = 'open' | 'in_progress' | 'done'

export interface RecommendationLink {
  id: string
  org_id: string
  index_id: string
  objective_code: string
  transition: Transition
  bullet_index: number
  playbook_action_id: string
  action_title: string
  owner: string | null
  due_at: string | null
  status: LinkStatus
  created_at: string
}

// ── History (§8.5) ──────────────────────────────────────────────────────────

export type HistoryKind = 'suggestion' | 'playbook' | 'manual' | 'snapshot' | 'confirm' | 'evidence' | 'coverage' | 'dependency'

export interface HistoryEvent {
  id: string
  org_id: string
  index_id: string
  objective_code: string | null // null = whole-index event (snapshot)
  kind: HistoryKind
  text: string
  actor: string
  at: string
  // The objective event this entry renders, for `#ev_{id}` deep links (I3 §13).
  event_id?: string | null
  detail?: string | null
}

// ── Export wizard (§10) ─────────────────────────────────────────────────────

export type AnswerState = 'prefilled' | 'suggested' | 'input' | 'declaration'

export interface DeclarationAnswer {
  choice: 'yes' | 'planned' | 'not_yet' | null
  reference: string
  target_date: string
}

export interface PartBSystem {
  id: string
  inventory_id: string | null
  name: string
  description: string
  operations: string
  crypto_findings: string
  business_owner: string
  migration_approach: string
  dependencies: string
  prefilled: boolean
}

export interface ExportDraft {
  id: string
  org_id: string
  index_id: string
  snapshot_id: string | null // null = Live
  template_id: string
  step: 1 | 2 | 3 | 4
  answers: Record<string, string>
  declarations: Record<string, DeclarationAnswer>
  systems: PartBSystem[]
  formats: { docx: boolean; pdf: boolean; json: boolean }
  updated_at: string
  created_by: string
}

export interface ExportRecord {
  id: string
  org_id: string
  index_id: string
  snapshot_id: string
  draft_id: string
  formats: string[]
  files: string[]
  created_by: string
  created_at: string
}

// ── Analytics events (Note 12) ──────────────────────────────────────────────

export type AnalyticsEvent =
  | { name: 'readiness.level_confirmed'; objective_code: string; level: LevelCode }
  | { name: 'readiness.level_overridden'; objective_code: string; level: LevelCode; from: LevelCode | null }
  | { name: 'readiness.evidence_added'; objective_code: string; source: EvidenceSource }
  | { name: 'readiness.assessment_saved'; snapshot_id: string; baseline: boolean }
  | { name: 'readiness.export_completed'; formats: string[]; snapshot_id: string }
  | { name: 'readiness.view_toggled'; view: 'cards' | 'heatmap' }
  | { name: 'readiness.quickview_opened'; objective_code: string }
  | { name: 'readiness.index_switched'; index_id: string }
  // Iteration 3 Note 10
  | { name: 'readiness.module_tab_opened'; tab: string }
  | { name: 'readiness.projection_viewed'; all_l3_by: string | null }
  | { name: 'readiness.submission_marked'; version: number }
  | { name: 'readiness.catalogue_opened'; technology_id: string | null }
  | { name: 'readiness.dependency_option_chosen'; option: 'wait' | 'pilot' | 'waiver' | 'playbook' }
  | { name: 'readiness.notification_opened'; kind: string; objective: string; event_id: string }
  // Iteration 5 Note 9
  | { name: 'readiness.summary_report_exported' }
  | { name: 'readiness.objectives_moved_opened' }
  | { name: 'readiness.score_tooltip_viewed'; surface: 'dashboard' | 'progress' | 'report' | 'rail' }
  | { name: 'readiness.evidence_archive_undone'; evidence_id: string }
  | { name: 'readiness.sync_summary_opened'; trigger: SyncRun['trigger'] }
  // Iteration 6 Note 8
  | { name: 'readiness.compare_pair_changed'; source: 'picker' | 'rail' | 'chart' }
  | { name: 'readiness.sparkline_hovered'; target: 'segment' | 'step' | 'drop' | 'stub' }
  // Iteration 6 (POC) Note 7
  | { name: 'readiness.profile_changed'; from: 'poc' | 'full'; to: 'poc' | 'full'; source: 'menu' | 'url' }
  | { name: 'readiness.locked_preview_viewed'; feature: string }
  | { name: 'readiness.locked_preview_dismissed'; feature: string }
  | { name: 'readiness.locked_item_activated'; feature: string }
  | { name: 'readiness.drawer_action'; action: 'set_level' | 'add_evidence'; code: string }

// ── Discovery facts consumed by the signal rules (§3.2) ─────────────────────

export interface DiscoveryFacts {
  connected: boolean
  inventories: Array<{
    id: string
    name: string
    description: string
    business_unit: string
    criticality: string
    tags: string[]
    prioritised: boolean // tagged CII / prioritised, or criticality critical/high
    cii?: boolean // Discovery data classification is CII (absent on facts persisted before it existed)
    asset_count: number | null
    // From qtz-readiness's Discovery read model (/discovery/systems); absent on
    // facts persisted before it existed.
    environment?: string
    has_cbom?: boolean
    last_scan_at?: string | null
  }>
  cbom_inventory_ids: string[] // inventories with ≥1 ingested source (CBOM present)
  source_count: number
  asset_count: number
  vulnerable_count: number
  cert_key_inventory: boolean
  vendor_components: Array<{ id: string; name: string; type: string; inventory_id: string | null }>
  quantum_risk: { vulnerable: number; partial: number; hybrid: number; safe: number; unknown: number } | null
}

// One Issuing CA qtz-playbook identified behind the certificates of the
// organisation's inventories: a third-party certificate dependency (EE-1).
export interface PlaybookIssuingCA {
  id: string
  ca_owner: string // e.g. DigiCert
  issuing_ca: string // e.g. WE1
  matched_by: string // fingerprint | key_identifier | name
  system_ids: string[] // readiness systems (Discovery inventories) it issued for
  inventory_names: string[]
}

// One row of the NIST remediation report: the Discovery assets of one name
// that one NIST rule covers, with the replacement option it names (TE-1).
export interface PlaybookRemediation {
  key: string
  asset: string
  asset_type: string
  nist_rule: string
  nist_remediation: string
  asset_count: number
  vulnerable_count: number // assets rated vulnerable or partial
  system_ids: string[]
}

export interface PlaybookFacts {
  // Plans are the actions this module creates from recommendations (Open
  // Question 9), so they are always available; `connected` stays true.
  connected: boolean
  // service_connected is qtz-readiness's /playbook/* read model: true once
  // it has synced the Issuing CA register from qtz-playbook (Module 3).
  service_connected: boolean
  plans: Array<{
    id: string
    title: string
    inventory_id: string | null
    status: 'draft' | 'evaluated' | 'in_progress' | 'done'
    owner: string | null
    due_at: string | null
    updated_at: string
  }>
  issuing_cas: PlaybookIssuingCA[]
  remediations: PlaybookRemediation[]
  standard: string | null // the remediation table's standard, e.g. NIST FIPS 203/204/205 (2024)
}
